import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpRequest } from './http-request';
import { catchError, finalize, firstValueFrom, Observable, of, tap, throwError } from 'rxjs';
import { StorageService } from 'src/app/utils';
import { StoragePrefix } from '@core/constant';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { createApiState } from '@core/interfaces';

import { ResetPassword } from 'src/app/auth/reset-password-layout/reset-password-step';
import { ResetPasswordStepService } from 'src/app/auth/reset-password-layout/reset-password-step.service';
import { ToastService } from './toast.service';
import { SignupStepService } from 'src/app/auth/signup/signup-step.service';
import { LoginStepService } from 'src/app/auth/login-layout/login-layout-step.service';
import { EP } from '@core/config';
import { environment } from '@env/environment';
import { IdleTimeoutService } from './idle-timeout.service';
import { AffiliationStepService } from 'src/app/auth/affiliation-layout/affiliation-step.service';
import { ApplicationStatusService } from './application-status.service';
import { HtmlSanitizeService } from '@core/utils/html-sanitize';
import { LoaderService } from './loader.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private httpReqServices = inject(HttpRequest);
  private _storage = inject(StorageService);
  private _appStatus = inject(ApplicationStatusService);
  private _htmlSanitize = inject(HtmlSanitizeService);
  private _resetPasswordStep = inject(ResetPasswordStepService);
  private _loader = inject(LoaderService);
  _toast = inject(ToastService);
  _signupStep = inject(SignupStepService);
  _loginStep = inject(LoginStepService);
  _affiliationStep = inject(AffiliationStepService);
  _idleTimeout = inject(IdleTimeoutService);
  private loggedIn = signal(false);
  public otpIdNumber: any = signal(null);
  public resetPasswordOtp: any = signal(null);
  maintenance = signal<boolean | null>(null);
  currentScreen = signal('kyc'); // first screen visible

  private _data: any = signal({
    signUpFormObj: null,
    signIn: null,
    signup: null,
    otp: null,
    terms: null,
    personal: null,
    KYC: null,
    financialInformation: null,
    setId: null,
    initiallyEligible: null,
    abhser: null,
    token: '',
  });

  private _dataLogin: any = signal({
    changeDevice: null,
    changeNumber: null,
  });

  router = inject(Router);

  data = computed(() => this._data());
  dataLogin = computed(() => this._dataLogin());

  otpReqData = createApiState();
  otpValidData = createApiState();
  otpChangeReqData = createApiState();
  otpChangeValidData = createApiState();
  updateProfile = createApiState();
  deActivateAccount = createApiState();
  deviceInfoReqData = createApiState();
  logOutReqData = createApiState();
  checkAppStatusReqData = createApiState();
  termAndConditionReqData = createApiState();
  privacyPolicyReqData = createApiState();
  yakeenReqData = createApiState();
  acceptTCReqData = createApiState();
  changeDeviceReqData = createApiState();
  changeDeviceOTPReqData = createApiState();
  changeNumberReqData = createApiState();
  changeNumberOTPReqData = createApiState();
  aprCalculationOTPReqData = createApiState();
  userAffReqData = createApiState();
  maintenanceReqData = createApiState();

  private termsFetchedLang: string | null = null;
  private termsInFlightLang: string | null = null;
  private privacyFetchedLang: string | null = null;
  private privacyInFlightLang: string | null = null;

  // http calls section start
  otpVerification(url: string, obj: any, param: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj, param);
  }

  yakeenStatus(url: string, obj: any): Observable<any> {
    return this.httpReqServices.post(url, obj);
  }

  tempSignup(url: string, obj: any, param: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj, param);
  }

  getScreenWithQuestion(url: string, param: any): Observable<any> {
    return this.httpReqServices.getParam(url, param);
  }

  getLandingDecision(url: string, param: any): Observable<any> {
    return this.httpReqServices.getParam(url, param);
  }
  _loanStarted(url: string, body: any): Observable<any> {
    return this.httpReqServices.postParam(url, body);
  }

  updateAndCheck(url: string, obj: any, param: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj, param);
  }

  _validateLoanFormula(url: string, obj: any, param: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj, param);
  }

  sendOTPSoapRequest(url: string, obj: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj);
  }

  updateAbsherStatus(url: string, obj: any): Observable<any> {
    const option: any = {
      responseType: 'text',
    };
    return this.httpReqServices.put(url, obj, option);
  }

  nafath(url: string, obj: any, param: any): Observable<any> {
    return this.httpReqServices.postParam(url, obj, param);
  }

  getOTPFroResetPassword(idNumber: number, isFromOtp: any) {
    this.otpReqData.start(); // sets loading = true, error = null
    this.httpReqServices
      // .post(
      //   `auth/customer/user/generateOtpForResetPassword?idNumber=${idNumber}`,
      //   {},
      // )
      .postParam(
        EP.GenerateOtpForResetPassword, {}, {
        params: { idNumber: idNumber }
      },

      )
      .pipe(
        finalize(() => {
          this.otpReqData.state.update((s) => ({ ...s, loading: false }));
        }),
      )
      .subscribe({
        next: (data: any) => {
          if (!data.error) {
            if (!isFromOtp) {
              this._resetPasswordStep.currentStep.set(ResetPassword.otp);
            }
            this.otpIdNumber.set(idNumber);
            this.otpReqData.success(data);
          }
        },
        error: (err) => {
          this.otpReqData.fail(err.error.message || 'Error fetching OTP');
        },
      });
  }

  _otpValidationAndResetPassword(obj: any) {
    this.otpValidData.start(); // sets loading = true, error = null
    this.httpReqServices
      .postParam(EP.OtpValidationAndResetPassword, obj)
      .pipe(
        finalize(() => {
          this.otpValidData.state.update((s) => ({ ...s, loading: false }));
        }),
      )
      .subscribe({
        next: (data: any) => {
          if (!data.error) {
            this._toast.success(data.message);
            this.otpValidData.success(data);
            this._resetPasswordStep.resetSteps();
            this.router.navigate(['./login']);
          }
        },
        error: (err: any) => {
          this.otpValidData.fail(
            (err && err.error.message) || 'Error fetching OTP',
          );
        },
      });
  }

  _checkAppStatus() {
    let personalObj: any = this._data().personal;
    return this._appStatus.fetch(personalObj.id);
  }

  /** RN checkLoanApplicationStatus / checkUserHasApplications — applicationCount for post-login routing. */
  _checkUserHasApplications() {
    const personalObj: any = this._data().personal;
    return this.httpReqServices.getParam(EP.CheckUserHasApplications, {
      params: { userId: personalObj.id },
    });
  }

  _updateUserBasicInfo(obj: any) {
    return this.httpReqServices.put(EP.UpdateUser, obj);
  }

  _updateUserProfile(payload: any, formObj: any, callFunction: Function) {
    this.updateProfile.start();
    this.httpReqServices
      .postParam(EP.TempUserUpdateUser, payload)
      .pipe(
        finalize(() => {
          this.updateProfile.state.update((s) => ({ ...s, loading: false }));
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (!res.error) {
            this._storage.set(StoragePrefix.PERSONAL_INFO, res.data);
            this.updateProfile.success(res);
            this._toast.success(res.message);
            if (callFunction && typeof callFunction === 'function') {
              callFunction(res);
            }
          } else {
            this.updateProfile.fail(res.message);
            // this.updateProfile.success({ error: res.error, message: res.message });
          }
        },
        error: (err: any) => {
          this.updateProfile.fail(
            err.message || 'error while updating user profile.',
          );
        },
      });
  }

  _deActivateAccount(obj: any) {
    this.deActivateAccount.start();
    // setTimeout(() => {
    //     this.deActivateAccount.state.update(s => ({ ...s, loading: false }));
    //     this.logout()
    // }, 1000);
    this.httpReqServices
      .delete(EP.DeleteUser, obj)
      .pipe(
        finalize(() => {
          this.deActivateAccount.state.update((s) => ({
            ...s,
            loading: false,
          }));
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (!res.error) {
            this._logout();
            this.deActivateAccount.success(res);
          } else {
            this.deActivateAccount.fail(res.message);
            // this.deActivateAccount.success({ error: res.error, message: res.message });
          }
        },
        error: (err: any) => {
          this.deActivateAccount.fail(
            err.message || 'error while deleting user profile.',
          );
        },
      });
  }

  getOTPFroChangePassword(idNumber: number) {
    this.otpChangeReqData.start(); // sets loading = true, error = null
    return this.httpReqServices
      // .post(
      //   `auth/customer/user/generateOtpForResetPassword?idNumber=${idNumber}`,
      //   {},
      // )
      .postParam(
        EP.GenerateOtpForResetPassword,
        {},
        {
          params: { 'idNumber': idNumber }
        }
      )
      .pipe(
        finalize(() => {
          this.otpChangeReqData.state.update((s) => ({ ...s, loading: false }));
        }),
      );
  }

  _otpValidationAndChangePassword(obj: any) {
    this.otpChangeValidData.start(); // sets loading = true, error = null
    return this.httpReqServices
      .postParam(EP.OtpValidationAndResetPassword, obj)
      .pipe(
        finalize(() => {
          this.otpChangeValidData.state.update((s) => ({
            ...s,
            loading: false,
          }));
        }),
      );
  }

  _saveDeviceInfo(obj: any) {
    this.deviceInfoReqData.start();
    return this.httpReqServices
      .postParam(EP.SaveDeviceInfo, obj)
      .pipe(
        finalize(() => {
          this.deviceInfoReqData.state.update((s) => ({
            ...s,
            loading: false,
          }));
        }),
      );
  }
  _logout() {
    if (this.logOutReqData.state().loading) {
      return;
    }

    this.logOutReqData.start();
    this._loader.start();
    this.httpReqServices
      .postParam(EP.Signout, {})
      .pipe(
        finalize(() => {
          this.logOutReqData.state.update((s) => ({ ...s, loading: false }));
          this._loader.stop();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (!res.error) {
            this.logOutReqData.success(res);
            this.logout();
          } else {
            this.logOutReqData.fail(res.message);
            // Still clear local session so the UI does not stay stuck logged-in
            this.logout();
          }
        },
        error: (err: any) => {
          this.logOutReqData.fail(
            err.message || 'error while updating user profile.',
          );
          this.logout();
        },
      });
  }

  _getTermAndCondition() {
    const lang = this._storage.get(StoragePrefix.LANGUAGE) || 'en';
    const state = this.termAndConditionReqData.state();

    if (state.data?.safeHtml && this.termsFetchedLang === lang) {
      if (state.loading) {
        this.termAndConditionReqData.state.update((s) => ({ ...s, loading: false }));
      }
      return;
    }
    if (this.termsInFlightLang === lang) {
      return;
    }

    this.termsInFlightLang = lang;
    if (!state.data?.safeHtml || this.termsFetchedLang !== lang) {
      this.termAndConditionReqData.start();
    }

    this.httpReqServices
      .getParam(EP.GetTermsByLanguage, {
        params: {
          policyType: StoragePrefix.TERMS_AND_CONDITIONS,
          lan: lang,
        },
      })
      .pipe(
        finalize(() => {
          if (this.termsInFlightLang === lang) {
            this.termsInFlightLang = null;
          }
          this.termAndConditionReqData.state.update((s) => ({
            ...s,
            loading: false,
          }));
        }),
      )
      .subscribe({
        next: (res: any) => {
          res.safeHtml = this._htmlSanitize.toSafeHtml(res.description);
          this.termsFetchedLang = lang;
          this.termAndConditionReqData.success(res);
        },
        error: (err: any) => {
          this.termAndConditionReqData.fail(
            err.message || 'error while fetching term and condition.',
          );
        },
      });
  }

  _getPrivacyPolicy() {
    const lang = this._storage.get(StoragePrefix.LANGUAGE) || 'en';
    const state = this.privacyPolicyReqData.state();

    if (state.data?.safeHtml && this.privacyFetchedLang === lang) {
      if (state.loading) {
        this.privacyPolicyReqData.state.update((s) => ({ ...s, loading: false }));
      }
      return;
    }
    if (this.privacyInFlightLang === lang) {
      return;
    }

    this.privacyInFlightLang = lang;
    if (!state.data?.safeHtml || this.privacyFetchedLang !== lang) {
      this.privacyPolicyReqData.start();
    }

    this.httpReqServices
      .getParam(EP.GetTermsByLanguage, {
        params: {
          policyType: StoragePrefix.PRIVACY_POLICY,
          lan: lang,
        },
      })
      .pipe(
        finalize(() => {
          if (this.privacyInFlightLang === lang) {
            this.privacyInFlightLang = null;
          }
          this.privacyPolicyReqData.state.update((s) => ({
            ...s,
            loading: false,
          }));
        }),
      )
      .subscribe({
        next: (res: any) => {
          res.safeHtml = this._htmlSanitize.toSafeHtml(res.description);
          this.privacyFetchedLang = lang;
          this.privacyPolicyReqData.success(res);
        },
        error: (err: any) => {
          this.privacyPolicyReqData.fail(
            err.message || 'error while fetching term and condition.',
          );
        },
      });
  }

  _acceptTerms(url: any, obj: any, param: any) {
    return this.httpReqServices.postParam(url, obj, param);
  }

  _getUserImage(url: any, param: any) {
    return this.httpReqServices.getParam(url, param);
  }

  _getLoanPolicyAgeLimits(url: any) {
    return this.httpReqServices.getParam(url);
  }

  _changeCustomerDevice(url: any, obj: any, param: any) {
    return this.httpReqServices.postParam(url, obj, param);
  }

  _generateOtpForChangeDevice(url: any, obj: any, param: any) {
    return this.httpReqServices.postParam(url, obj, param);
  }

  _verifyCustomerDevice(url: any, obj: any) {
    return this.httpReqServices.postParam(url, obj);
  }

  _changeCustomerMobileNumber(url: any, obj: any) {
    return this.httpReqServices.postParam(url, obj);
  }

  _verifyCustomerMobileNumber(url: any, obj: any) {
    return this.httpReqServices.postParam(url, obj);
  }

  _generateOtpForChangeMobileNumber(url: any, obj: any) {
    return this.httpReqServices.postParam(url, obj);
  }
  _aprCalculation(url: any, param: any) {
    return this.httpReqServices.getParam(url, param);
  }

  _apiCall(url: any) {
    return this.httpReqServices.getParam(url);
  }

  loadStatus(url: any) {
    this.maintenanceReqData.start();
    return firstValueFrom(
      this.httpReqServices.getParam<any>(url, {
        params: { key: environment.UNDER_MAINTENANCE }
      }).pipe(
        finalize(() => {
          this.maintenanceReqData.state.update((s: any) => ({ ...s, loading: false }));
        })
      )
    ).then(res => {
      this.maintenanceReqData.success(res);
      // res?.value = "true";
      this.maintenance.set(res?.value === "true");
    }).catch((err) => {
      this.maintenanceReqData.fail(err);
      this.maintenance.set(false);
    });
  }

  _getIpAddress() {
    return this.httpReqServices.getParam(`${environment.ipAddressUrl}`, {
      params: {
        format: 'json'
      }
    })
  }

  _getLocationByLatLng(url: any, params: any) {
    return this.httpReqServices.getParam(url, {
      params: params
    })
  }

  _userAffiliation(url: any, obj: any) {
    return this.httpReqServices.post(url, obj);
  }

  _getLoanPolicySalaryLimits(url: any) {
    return this.httpReqServices.getParam(url);
  }

  // http calls section ends

  // temp data storage for signup step start

  setSignupData(info: any) {
    this._data.update((v: any) => ({ ...v, signup: info }));
  }

  setSignUpFormData(info: any) {
    this._data.update((v: any) => ({ ...v, signUpFormObj: info }));
  }

  setPersonalData(info: any) {
    this._data.update((v: any) => ({ ...v, personal: info }));
  }

  setSigninDetail(info: any) {
    this._data.update((v: any) => ({ ...v, signIn: info }));
  }

  setUserSetId(info: any) {
    this._data.update((v: any) => ({ ...v, setId: info }));
  }

  setAccount(info: any) {
    this._data.update((v: any) => ({ ...v, account: info }));
  }

  setChangeDevice(device: any) {
    this._dataLogin.update((v: any) => ({ ...v, changeDevice: device }));
  }

  setChangeNumber(number: any) {
    this._dataLogin.update((v: any) => ({ ...v, changeNumber: number }));
  }

  completeSignup() {
    const finalToken = 'token'; // returned from API

    this._storage.set('token', finalToken);
    this._storage.remove('signup_temp_token'); // cleanup
    // this._authService.setToken(finalToken);

    // this.router.navigate(['/dashboard']);
  }

  // temp data storage for signup step end

  // temp token start
  setToken(token: string) {
    this._data.update((v: any) => ({ ...v, token }));
    this._storage.set(StoragePrefix.SIGNUP_TEMP_TOKEN, token); // optional, persist between reloads
  }

  get token() {
    return (
      this._data().token || this._storage.get(StoragePrefix.SIGNUP_TEMP_TOKEN)
    );
  }
  // temp token end

  // otpVerification(idNumber: string, obj: any): Observable<any> {
  //   return this.http.post(`${EP.IdNumberValidation}?idNumber=${idNumber}`, obj);
  // }

  login(username: string, password: string) {
    if (username === 'admin' && password === '123') {
      this.loggedIn.set(true);
      this._storage.set(StoragePrefix.TOKEN, 'fake-jwt-token');
    }
  }

  logout() {
    const lang = this._storage.get(StoragePrefix.LANGUAGE);
    this.loggedIn.set(false);
    this._signupStep.resetSteps();
    this._loginStep.resetSteps();
    this._affiliationStep.resetSteps();
    this._storage.clear();
    this._storage.set(StoragePrefix.LANGUAGE, lang);
    this._idleTimeout.onLogout();
    this.router.navigate(['/login']);
    this.setSignUpFormData(null);
  }

  resetSignals() {
    this._signupStep.goToStep('signup');
    this._loginStep.resetSteps();
  }

  isLoggedIn(): boolean {
    return !!this._storage.get(StoragePrefix.TOKEN);
  }

  isLoggedInTemp(): boolean {
    return !!this._storage.get(StoragePrefix.SIGNUP_TEMP_TOKEN);
  }
}
